import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import { useToastStore } from '../../../store/toast.store';
import * as supportService from '../services/support.service';
import type { SupportFeedbackItemDTO, SupportFeedbackStatus } from '../types/support.types';

function resolveError(error: unknown): string {
  if (!isApiError(error)) {
    return '未知错误';
  }

  const map: Record<string, string> = {
    unauthorized: '登录状态已失效，请重新登录',
    admin_role_required: '无管理员权限',
    'invalid support feedback status filter': '状态筛选不合法',
    'invalid support feedback status': '状态值不合法',
    'support feedback not found': '反馈记录不存在',
    'support feedback admin comment is required': '请填写复测说明',
    'support feedback content is too long': '文字描述过长',
    'support feedback image count exceeded': '图片数量超过限制',
    'support feedback image is invalid': '图片格式不合法',
    'support feedback image is too large': '图片过大',
    support_feedback_payload_too_large: '提交内容过大，请减少图片数量或重新选择图片',
    network_error: '网络连接失败，请检查网络',
  };

  return map[error.message] ?? `请求失败 (${error.message})`;
}

export function useSupportFeedbackAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;
  const status = (searchParams.get('status') || 'pending') as '' | SupportFeedbackStatus;
  const userId = searchParams.get('userId') || '';

  const addToast = useToastStore((s) => s.addToast);
  const [items, setItems] = useState<SupportFeedbackItemDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(12);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<SupportFeedbackItemDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (targetPage: number, targetStatus: '' | SupportFeedbackStatus, targetUserId: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await supportService.listSupportFeedbacks({
        page: targetPage,
        pageSize,
        userId: targetUserId,
        status: targetStatus,
      });
      setItems(result.items ?? []);
      setTotal(result.total);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const result = await supportService.getSupportFeedbackDetail(id);
      setSelectedItem(result);
    } catch (e) {
      addToast('error', resolveError(e));
      setSelectedItem(null);
    } finally {
      setDetailLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void load(page, status, userId);
  }, [load, page, status, userId]);

  useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null);
      setSelectedItem(null);
      return;
    }
    if (selectedId === null || !items.some((item) => item.id === selectedId)) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  useEffect(() => {
    if (selectedId === null) {
      setSelectedItem(null);
      return;
    }
    void loadDetail(selectedId);
  }, [loadDetail, selectedId]);

  const setStatus = useCallback((value: '' | SupportFeedbackStatus) => {
    updateParams({ status: value || null, page: null });
  }, [updateParams]);

  const setUserId = useCallback((value: string) => {
    updateParams({ userId: value || null, page: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ page: value > 1 ? String(value) : null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({ status: 'pending' }, { replace: true });
  }, [setSearchParams]);

  const reload = useCallback(async () => {
    await load(page, status, userId);
  }, [load, page, status, userId]);

  const updateStatus = useCallback(async (nextStatus: SupportFeedbackStatus) => {
    if (selectedId === null) {
      return;
    }

    setSubmitting(true);
    try {
      await supportService.updateSupportFeedbackStatus(selectedId, nextStatus);
      addToast('success', '反馈状态已更新');
      await load(page, status, userId);
      if (selectedId !== null) {
        await loadDetail(selectedId);
      }
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSubmitting(false);
    }
  }, [addToast, load, loadDetail, page, selectedId, status, userId]);

  const reopenWithComment = useCallback(async (content: string, imageDataURLs: string[]) => {
    if (selectedId === null) {
      return false;
    }

    setSubmitting(true);
    try {
      const result = await supportService.reopenSupportFeedback(selectedId, {
        content,
        image_data_urls: imageDataURLs,
      });
      setSelectedItem(result);
      addToast('success', '已追加说明并恢复为待处理');
      await load(page, status, userId);
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, load, page, selectedId, status, userId]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items,
    total,
    page,
    totalPages,
    userId,
    status,
    selectedId,
    selectedItem,
    loading,
    detailLoading,
    error,
    submitting,
    setPage,
    setUserId,
    setStatus,
    setSelectedId,
    resetFilters,
    reload,
    updateStatus,
    reopenWithComment,
  };
}
